import { useMutation, useQueryClient } from '@tanstack/react-query';
import { unsaveJobRecommendation } from '@worker/services/MyJobs/api';
import {
  WorkerJobCommonResultDTO,
  WorkerJobCommonResultsResponse,
  SavedJobsResponse,
  MyJobsTabsEnum,
} from '@worker/types/myJobs.type';
import {
  updateWorkerJobSavedState,
  removeSavedJob,
  extractErrorMessage,
} from '@worker/helpers/my-jobs-helpers';
import QueryKeys from '@constants/queryKeys';
import useNotification from '@hooks/useNotification';
import { useWorkerStore } from '@worker/stores';

/**
 * Hook to unsave a job recommendation
 * Updates cache on success only - no optimistic updates or query invalidation
 * Tab-aware: Removes job from list on SAVED tab, clears savedAt on other tabs
 */
export function useUnsaveJobMutation() {
  const queryClient = useQueryClient();
  const currentTab = useWorkerStore((state) => state.currentTab);
  const { showInfoNotification, showErrorNotification } = useNotification();

  return useMutation({
    mutationKey: [QueryKeys.MyJobs.UNSAVE_JOB],
    mutationFn: (jobId: number) => unsaveJobRecommendation(jobId),
    onSuccess: (_response, jobId) => {
      // Update ALL job recommendation caches
      queryClient.setQueriesData<WorkerJobCommonResultsResponse>(
        { queryKey: [QueryKeys.MyJobs.JOB_RECOMMENDATIONS] },
        (data) => {
          if (!data?.results) return data;

          if (currentTab === MyJobsTabsEnum.SAVED) {
            // SAVED tab: Remove the job from the list (no longer saved)
            return removeSavedJob(
              data as SavedJobsResponse,
              jobId
            ) as WorkerJobCommonResultsResponse;
          }
          // NEW & APPLIED tabs: Keep job in list, clear saved status
          return updateWorkerJobSavedState(data, jobId, null);
        }
      );

      queryClient.setQueriesData<WorkerJobCommonResultDTO>(
        { queryKey: [QueryKeys.MyJobs.JOB_RECOMMENDATION_BY_ID] },
        (data) => {
          if (!data || data.job?.id !== jobId) return data;
          return { ...data, workerSavedJob: undefined };
        }
      );

      showInfoNotification('Job removed from saved list.', {
        title: 'Removed',
      });
    },
    onError: (err: unknown) => {
      // No rollback needed - cache was never updated
      showErrorNotification(
        extractErrorMessage(err, 'Failed to unsave job. Please try again.')
      );
    },
  });
}

export default useUnsaveJobMutation;
